/**
 * Atalhos de filtro acima da tabela (ex.: Pendentes, Vencidas). O atalho e o Status
 * do painel de filtros mexem no mesmo recorte: ligar um desliga o outro, senão
 * "Vencidas" + "Pago" daria uma lista vazia sem explicação.
 */
export interface QuickFilterOption {
  value: string;
  label: string;
}

export interface QuickFilterSummary {
  count: number;
  amount: number;
}

interface QuickFilterable {
  pageIndex: number;
  quickFilter?: string;
  dueMonth?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  query?: Record<string, any>;
}

export function applyQuickFilter<T extends QuickFilterable>(prev: T, value: string): T {
  const { status, ...query } = prev.query ?? {};
  return { ...prev, pageIndex: 0, query, quickFilter: value || undefined };
}

export function applyColumnFilter<T extends QuickFilterable>(prev: T, accessor: string, value: any): T {
  return {
    ...prev,
    query: { ...prev.query, [accessor]: value },
    ...(accessor === 'status' && { quickFilter: undefined }),
  };
}

/**
 * Mês é um atalho que se desliga clicando de novo; não mexe no de situação. Mês e
 * intervalo do painel são o mesmo recorte (vencimento): ligar um desliga o outro.
 */
export function applyDueMonth<T extends QuickFilterable>(prev: T, value: string): T {
  return {
    ...prev,
    pageIndex: 0,
    dueMonth: prev.dueMonth === value ? undefined : value,
    dueDateFrom: undefined,
    dueDateTo: undefined,
  };
}

/** Uma ponta do intervalo de vencimento do painel (`yyyy-MM-dd`; vazio tira a ponta). */
export function applyDueDate<T extends QuickFilterable>(
  prev: T,
  field: 'dueDateFrom' | 'dueDateTo',
  value: string,
): T {
  return { ...prev, pageIndex: 0, [field]: value || undefined, dueMonth: undefined };
}

/**
 * O que os contadores dos atalhos levam — e a chave do cache deles. Fica de fora
 * o que não muda a conta: página, ordenação, o próprio atalho e o Status do
 * painel (o servidor o ignora no resumo).
 */
export function quickFiltersSummaryParams(
  filter: QuickFilterable & {
    filter?: string;
    customerId?: string;
    companyId?: string;
    maquinetaId?: string;
    promoterId?: string;
    initialDate?: string;
    finalDate?: string;
  },
) {
  const { status, ...query } = filter.query ?? {};
  return {
    filter: filter.filter,
    customerId: filter.customerId,
    companyId: filter.companyId,
    maquinetaId: filter.maquinetaId,
    promoterId: filter.promoterId,
    initialDate: filter.initialDate,
    finalDate: filter.finalDate,
    query,
    dueMonth: filter.dueMonth,
    dueDateFrom: filter.dueDateFrom,
    dueDateTo: filter.dueDateTo,
  };
}
